import { Component } from "@angular/core";
import { ActiveStatus, ActiveStatusEnum, FeatureChoice, Product, Manufacturer, ProductFeatureChoice } from "app/model/entities/entity-model";
import { ProductType } from "app/model/entities/product-type";
import { DialogBaseComponent, DialogParams } from "app/shared/dialog-base.component";
import { UtilFns } from "app/shared/util-fns";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { DomainService } from "app/shared/domain.service";
import { ProductExposureApiService, ProductExposureFns } from "app/shared/product-exposure-api.service";
import { EntityState } from "breeze-client";

export interface EditProductDialogParams extends DialogParams {
  // empty product means to add one - in which case productType must be entered..
  product?: Product;
  productType?: ProductType;
}

@Component({
  templateUrl: "./edit-product-dialog.component.html",
  standalone: false,
})
export class EditProductDialogComponent extends DialogBaseComponent<EditProductDialogParams> {
  activeStatuses: ActiveStatus[];
  product: Product;
  productType: ProductType;
  featureChoices: FeatureChoice[];
  availableFeatureChoices: FeatureChoice[];
  isAdding: boolean;
  oldUseSize: string;
  private statusBeforeChange: ActiveStatusEnum;

  constructor(
    public config: DynamicDialogConfig<EditProductDialogParams>,
    public dialogRef: DynamicDialogRef,
    public domainService: DomainService,
    private productExposureApi: ProductExposureApiService
  ) {
    super(config, dialogRef, domainService);
    this.prepare();
  }

  private async prepare() {
    this.product = this.params.product;
    this.activeStatuses = this.dbQueryService.getAllCached(ActiveStatus).filter(
      (s) => s.id !== ActiveStatusEnum.Hold || this.product?.activeStatusId === ActiveStatusEnum.Hold,
    );
    if (this.product == null) {
      this.isAdding = true;
      this.productType = this.params.productType;
      this.product = this.createNewProductUnchanged();
      if (this.product == null) {
        this.dialogService.showOkMessage(
          "No Feature Choices",
          "Cannot create a new product because there are no available feature choices."
        );
        this.dialogRef.close(false);
        return;
      }
    } else {
      this.productType = this.product.productType;
    }
    const title = this.isAdding ? "Add Product" : "Edit Product";

    Object.assign(this.config, {
      header: title,
      width: "850px",
      height: "575px",
    });

    const allManufs = await this.uow.queryAll(Manufacturer, "Manufacturers");
    await this.dbQueryService.getProductType(this.productType.id);

    this.statusBeforeChange = this.product.activeStatusId;
    this.isPageReady = true;
  }

  /** Inactive on a product with open work in Uniformax or Proximity asks first, and offers Phaseout. */
  async onStatusChange(statusId: ActiveStatusEnum) {
    if (statusId === ActiveStatusEnum.Inactive && !this.isAdding && !(await this.confirmInactive())) {
      this.product.activeStatusId = this.statusBeforeChange;
      return;
    }
    this.statusBeforeChange = statusId;
  }

  private async confirmInactive() {
    const use = await this.wrapBusy(() => this.productExposureApi.getForProducts([this.product.id]).catch(() => null));
    const open = use ? ProductExposureFns.inFlightList(use) : "";
    if (use && use.ProximityChecked && !open) return true;

    const message =
      (use == null ? "The system cannot count the open work on this product." : "")
      + (open ? `This product has open work:${open}` : "")
      + (use && !use.ProximityChecked ? "<br/>The system cannot check Proximity." : "")
      + "<br/><br/>If you make the product Inactive, the open work cannot finish."
      + " To let the open work finish, put the product on Phaseout."
      + "<br/><br/>Do you want to make the product Inactive?";
    return this.dialogService.yesNoDialog("Make Inactive", message, "Make Inactive", "Cancel");
  }

  private createNewProductUnchanged() {
    const availableFcCombos = this.getAvailableFeatureChoiceCombos(this.productType);
    if (availableFcCombos.length == 0) {
      return;
    }
    const prod = this.dbSaveService.createEntity(Product, {
      modTs: new Date(),
      modUserInit: this.authUser.initials,
      productTypeId: this.productType.id,
      activeStatusId: 1,
    });
    // NOTE: setUnchanged must occur on a separate line from the createEntity
    // because we want to insure that the prod is marked for new id generation
    // this will not happen if the entity is created in an unchanged state.
    // This is not an issue for pfc's below because they have no autogenerated id.
    prod.entityAspect.setUnchanged();

    availableFcCombos[0].forEach((fc) => {
      const pfc = this.dbSaveService.createEntity(
        ProductFeatureChoice,
        {
          productId: prod.id,
          featureChoiceId: fc.id,
        },
        EntityState.Unchanged
      );
    });

    return prod;
  }

  getAllFeatureChoiceCombos(productType: ProductType) {
    const choiceLists = productType.productTypeFeatureMaps.map((ptfMap) => {
      return ptfMap.feature.featureChoices;
    });
    return UtilFns.cartesianProduct(choiceLists) as FeatureChoice[][];
  }

  getAvailableFeatureChoiceCombos(productType: ProductType) {
    const allCombos = this.getAllFeatureChoiceCombos(productType);
    const curCombos = productType.products.map((p) => p.productFeatureChoices.map((x) => x.featureChoice));
    const availableCombos = allCombos.filter((ac) => !curCombos.some((cc) => this.areFcCombosEqual(ac, cc)));
    return availableCombos;
  }

  areFcCombosEqual(c1: FeatureChoice[], c2: FeatureChoice[]) {
    return c1.every((fc) => c2.includes(fc));
  }

  async ok() {
    if (this.isAdding) {
      this.product.entityAspect.setAdded();
      this.product.productFeatureChoices.forEach((pfc) => {
        pfc.entityAspect.setAdded();
      });
    }
    const ok = await this.save();
    if (!ok) {
      return;
    }
    this.dialogRef.close(true);
  }

  async save() {
    // remove excess spaces
    const prod = this.product;

    const isValid = await this.validate();
    if (!isValid) {
      return false;
    }

    try {
      const sr = await this.dbSaveService.saveChanges();
    } catch (e) {
      this.uow.rollback();
      return false;
    }
    return true;
  }

  async validate() {
    const prod = this.product;
    if (prod.productFeatureChoices.length == 0) {
      await this.dialogService.showOkMessage("Missing Feature Choice", `Cannot save because you have not specified a Feature Choice.`);
      return false;
    }

    let matchingEntity = await this.dbQueryService.getMatchingProduct(prod.productTypeId, prod.productFeatureChoices);
    if (matchingEntity != null && matchingEntity !== prod) {
      await this.dialogService.showOkMessage(
        "Duplicate Feature set",
        `Cannot save because this is a duplicate of an existing feature set for this product type.`
      );
      return false;
    }

    const ea = this.product.entityAspect;
    if (!ea.validateEntity()) {
      const vErrors = ea.getValidationErrors();
      const errMsg = vErrors.map((ve) => ve.errorMessage).join("<br>");
      await this.dialogService.showOkMessage("Validation Error(s)", errMsg);
      return false;
    }
    return true;
  }

  async cancel() {
    // insures that we don't try to redisplay the page after the reject changes.
    this.isPageReady = false;
    if (this.isAdding) {
      this.product.productFeatureChoices.forEach((pfc) => pfc.entityAspect.setDetached());
      this.product.entityAspect.setDetached();
    }

    this.uow.rollback();
    this.dialogRef.close(false);
  }
}
